import { getInfluxMonitorStatus, isActiveInfluxTarget, isInfluxUpStatus } from './influxStatus';
import { isPhase1IncidentAcknowledged } from './incidentAck';

const getTargetStatus = (target) => {
  const raw = target?.last_status ?? target?.status;
  return raw ? String(raw).toUpperCase() : null;
};

const isActiveTarget = (target) =>
  target?.is_active !== false && target?.is_active !== 0 && target?.is_active !== '0';

function isHardwareUnhealthy(data) {
  if (!data?.summary) return false;
  const issues = data.summary.issues;
  const overall = data.summary.overall;
  if (Array.isArray(issues) && issues.length > 0) return true;
  if (overall && overall !== 'Healthy' && overall !== 'OK') return true;
  return false;
}

function buildPhase1IssueRows(targets, influxTargets, hardwareHealthMap, agents, includeRow) {
  const rows = [];
  const agentNameById = {};
  (agents || []).forEach((a) => {
    agentNameById[a.agent_id] = a.agent_name || a.agent_id;
  });

  (targets || []).filter(isActiveTarget).forEach((t) => {
    const status = getTargetStatus(t);
    if (status !== 'DOWN' && status !== 'ERROR') return;
    if (!includeRow('monitor', t.id, status)) return;
    rows.push({
      key: `monitor-${t.id}`,
      kind: 'monitor',
      targetId: t.id,
      name: t.name || t.host || String(t.id),
      status,
      lastCheck: t.last_check || t.lastCheck,
    });
  });

  (influxTargets || []).filter(isActiveInfluxTarget).forEach((t) => {
    const status = getInfluxMonitorStatus(t);
    if (status !== 'ANOMALY' && status !== 'DOWN' && status !== 'ERROR') return;
    if (!includeRow('influx', t.id, status)) return;
    rows.push({
      key: `influx-${t.id}`,
      kind: 'influx',
      targetId: t.id,
      name: t.name || `Influx #${t.id}`,
      status,
      lastCheck: t.last_check || t.lastCheck,
    });
  });

  Object.entries(hardwareHealthMap || {}).forEach(([agentId, hw]) => {
    if (!isHardwareUnhealthy(hw)) return;
    const status = hw.summary?.overall || 'Warning';
    if (!includeRow('hardware', agentId, status)) return;
    rows.push({
      key: `hardware-${agentId}`,
      kind: 'hardware',
      targetId: agentId,
      name: hw.agent_name || agentNameById[agentId] || agentId,
      status,
      lastCheck: hw.timestamp,
    });
  });

  return rows.sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

/** Logs Alert — all live DOWN / ANOMALY / unhealthy (ignore acknowledge). */
export function buildPhase1LiveIssueList(targets, influxTargets, hardwareHealthMap, agents = []) {
  return buildPhase1IssueRows(
    targets,
    influxTargets,
    hardwareHealthMap,
    agents,
    () => true
  );
}

/**
 * Acknowledge card — monitor & influx only (user pressed Ack, still unhealthy).
 */
export function buildPhase1AcknowledgedIssueList(targets, influxTargets, _hardwareHealthMap, agents = []) {
  return buildPhase1IssueRows(
    targets,
    influxTargets,
    {},
    agents,
    (kind, id) => {
      const type = kind === 'monitor' ? 'target-status' : 'influxdb';
      return isPhase1IncidentAcknowledged(type, id);
    }
  );
}

export function patchHardwareHealthMap(prev, data) {
  const agentId = data?.agent_id ?? data?.agentId;
  if (!agentId) return prev;
  const next = { ...prev };
  if (isHardwareUnhealthy(data)) {
    next[agentId] = data;
  } else {
    delete next[agentId];
  }
  return next;
}

export function isInfluxRecoveryStatus(status) {
  return isInfluxUpStatus(status);
}

export { getTargetStatus, isActiveTarget, isHardwareUnhealthy };
